import { useCallback, useEffect, useState } from 'react';
import { Trophy, ArrowsClockwise, Fire, Medal, X, ChartLineUp } from '@phosphor-icons/react';
import api from '../lib/api';
import { extractErrorMessage } from '../lib/errors';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n/I18nContext';
import { Button } from '../components/ui/button';
import BadgeGrid from '../components/gamification/BadgeGrid';
import ActivityTimelineChart from '../components/gamification/ActivityTimelineChart';
import { toast } from 'sonner';

const SORT_KEYS = ['lines_added', 'commits', 'comments', 'tickets_created'];
const PERIOD_KEYS = ['all_time', 'week', 'month'];

function initials(name, email) {
  const src = name || email || '?';
  return src
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function StatPill({ label, value, rank }) {
  return (
    <div className="border border-zinc-200 bg-white p-4 rounded-sm">
      <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-zinc-500 mb-1">{label}</div>
      <div className="font-display text-2xl font-black tabular-nums">{value.toLocaleString()}</div>
      {rank > 0 && (
        <div className="text-[11px] text-zinc-500 mt-1 font-mono">#{rank}</div>
      )}
    </div>
  );
}

export default function StatsDashboard() {
  const { user } = useAuth();
  const t = useT();
  const [myStats, setMyStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [sort, setSort] = useState('lines_added');
  const [period, setPeriod] = useState('all_time');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activityTimeline, setActivityTimeline] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [meRes, lbRes] = await Promise.all([
        api.get('/stats/me', { params: { period } }),
        api.get('/stats/leaderboard', { params: { period, sort, limit: 50, offset: 0 } }),
      ]);
      setMyStats(meRes.data);
      setLeaderboard(lbRes.data);
    } catch (err) {
      toast.error(extractErrorMessage(err, t('stats.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [sort, period, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!selectedUser) {
      setActivityTimeline(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setActivityLoading(true);
      try {
        const { data } = await api.get(`/stats/users/${selectedUser.user_id}/activity`, {
          params: { period },
        });
        if (!cancelled) setActivityTimeline(data);
      } catch (err) {
        if (!cancelled) {
          toast.error(extractErrorMessage(err, t('stats.activityLoadFailed')));
          setActivityTimeline(null);
        }
      } finally {
        if (!cancelled) setActivityLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedUser, period, t]);

  const handleRowClick = (row) => {
    if (selectedUser?.user_id === row.user_id) {
      setSelectedUser(null);
      return;
    }
    setSelectedUser(row);
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/stats/sync');
      toast.success(data.message || t('stats.syncDone'));
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, t('stats.syncFailed')));
    } finally {
      setSyncing(false);
    }
  };

  const sortLabel = (key) => {
    const map = {
      lines_added: t('stats.linesAdded'),
      commits: t('stats.commits'),
      comments: t('stats.comments'),
      tickets_created: t('stats.tickets'),
    };
    return map[key] || key;
  };

  const periodLabel = (key) => {
    const map = {
      all_time: t('stats.periodAllTime'),
      week: t('stats.periodWeek'),
      month: t('stats.periodMonth'),
    };
    return map[key] || key;
  };

  const isAllTime = period === 'all_time';

  if (loading && !myStats) {
    return (
      <div className="flex-1 flex items-center justify-center text-zinc-500" data-testid="stats-loading">
        {t('common.loading')}
      </div>
    );
  }

  const earnedBadges = (myStats?.badges || []).filter((b) => b.earned);

  return (
    <div className="flex-1 overflow-y-auto bg-zinc-50 p-6 md:p-10" data-testid="stats-dashboard">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Trophy size={22} weight="fill" className="text-[#0055ff]" />
              <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-zinc-500">
                {t('stats.badge')}
              </span>
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">{t('stats.title')}</h1>
            <p className="text-sm text-zinc-600 mt-2 max-w-xl">{t('stats.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            {leaderboard?.last_synced_at && (
              <span className="text-[11px] text-zinc-500 font-mono hidden sm:inline">
                {t('stats.lastSync')}: {new Date(leaderboard.last_synced_at).toLocaleString()}
              </span>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={sync}
              disabled={syncing}
              data-testid="stats-sync-btn"
              className="rounded-sm"
            >
              <ArrowsClockwise size={14} className={`mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? t('stats.syncing') : t('stats.sync')}
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6" data-testid="stats-period-filters">
          {PERIOD_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              data-testid={`stats-period-${key}`}
              onClick={() => setPeriod(key)}
              className={`text-xs px-3 py-1.5 rounded-sm border font-semibold transition-colors ${
                period === key
                  ? 'bg-[#0055ff] text-white border-[#0055ff]'
                  : 'bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400'
              }`}
            >
              {periodLabel(key)}
            </button>
          ))}
        </div>

        {myStats && (
          <section className="mb-8" data-testid="stats-my-card">
            <div className="border border-zinc-200 bg-white rounded-sm p-5 md:p-6">
              <div className="flex items-center gap-3 mb-5">
                <span
                  style={{ backgroundColor: myStats.color }}
                  className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold text-white"
                >
                  {initials(myStats.name, myStats.email)}
                </span>
                <div>
                  <div className="font-display font-bold">{myStats.name}</div>
                  <div className="text-[11px] text-zinc-500 font-mono">{myStats.email}</div>
                </div>
                <div className="ml-auto flex items-center gap-1 text-orange-600">
                  <Fire size={18} weight="fill" />
                  <span className="font-display font-black text-lg tabular-nums">{myStats.current_streak}</span>
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500 ml-1">
                    {isAllTime ? t('stats.streak') : t('stats.activeDays')}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 mb-5">
                <StatPill label={t('stats.linesAdded')} value={myStats.lines_added} rank={myStats.rank_lines_added} />
                <StatPill label={t('stats.commits')} value={myStats.commits} rank={myStats.rank_commits} />
                <StatPill label={t('stats.comments')} value={myStats.comments} rank={myStats.rank_comments} />
                <StatPill label={t('stats.tickets')} value={myStats.tickets_created} rank={myStats.rank_tickets_created} />
              </div>

              {isAllTime && (
                <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-zinc-500 mb-2">
                  {t('stats.longestStreak')}: {myStats.longest_streak} {t('stats.days')}
                </div>
              )}

              {isAllTime && (
                <>
                  <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-zinc-500 mb-3 flex items-center gap-1">
                    <Medal size={12} weight="fill" className="text-amber-500" /> {t('stats.badges')} ({earnedBadges.length}/{myStats.badges?.length || 0})
                  </div>
                  <BadgeGrid badges={myStats.badges || []} lockedLabel={t('stats.badgeLocked')} />
                </>
              )}
            </div>
          </section>
        )}

        <p className="text-[11px] text-zinc-500 mb-4">{t('stats.emailHint')}</p>
        <p className="text-[11px] text-zinc-500 mb-4">{t('stats.activityHint')}</p>

        <section data-testid="stats-leaderboard">
          <div className="flex flex-wrap gap-2 mb-3">
            {SORT_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                data-testid={`stats-sort-${key}`}
                onClick={() => setSort(key)}
                className={`text-[11px] px-2.5 py-1 rounded-sm border transition-colors ${
                  sort === key
                    ? 'bg-black text-white border-black'
                    : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'
                }`}
              >
                {sortLabel(key)}
              </button>
            ))}
          </div>

          <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
                  <th className="text-left py-2 px-3 font-semibold w-12">#</th>
                  <th className="text-left py-2 px-3 font-semibold">{t('stats.player')}</th>
                  <th className="text-right py-2 px-3 font-semibold">{t('stats.linesAdded')}</th>
                  <th className="text-right py-2 px-3 font-semibold">{t('stats.commits')}</th>
                  <th className="text-right py-2 px-3 font-semibold">{t('stats.comments')}</th>
                  <th className="text-right py-2 px-3 font-semibold">{t('stats.tickets')}</th>
                  <th className="text-right py-2 px-3 font-semibold">
                    {isAllTime ? t('stats.streak') : t('stats.activeDays')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(leaderboard?.entries || []).length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-zinc-500 text-xs">
                      {t('stats.empty')}
                    </td>
                  </tr>
                ) : (
                  leaderboard.entries.map((row) => {
                    const isMe = row.user_id === user?.id;
                    const isSelected = selectedUser?.user_id === row.user_id;
                    return (
                      <tr
                        key={row.user_id}
                        data-testid={`stats-row-${row.user_id}`}
                        onClick={() => handleRowClick(row)}
                        className={`border-b border-zinc-100 last:border-0 cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-[#0055ff]/10'
                            : isMe
                              ? 'bg-[#0055ff]/5 hover:bg-[#0055ff]/10'
                              : 'hover:bg-zinc-50'
                        }`}
                      >
                        <td className="py-2.5 px-3 font-mono text-xs text-zinc-500">{row.rank}</td>
                        <td className="py-2.5 px-3">
                          <div className="flex items-center gap-2">
                            <span
                              style={{ backgroundColor: row.color }}
                              className="w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-semibold text-white flex-shrink-0"
                            >
                              {initials(row.name, '')}
                            </span>
                            <span className={`truncate ${isMe ? 'font-semibold' : ''}`}>{row.name}</span>
                          </div>
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono tabular-nums">{row.lines_added.toLocaleString()}</td>
                        <td className="py-2.5 px-3 text-right font-mono tabular-nums">{row.commits}</td>
                        <td className="py-2.5 px-3 text-right font-mono tabular-nums">{row.comments}</td>
                        <td className="py-2.5 px-3 text-right font-mono tabular-nums">{row.tickets_created}</td>
                        <td className="py-2.5 px-3 text-right font-mono tabular-nums">{row.current_streak}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {selectedUser && (
            <div
              className="mt-4 border border-zinc-200 bg-white rounded-sm p-4 md:p-5"
              data-testid="stats-activity-panel"
            >
              <div className="flex items-center gap-3 mb-4">
                <span
                  style={{ backgroundColor: selectedUser.color }}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-semibold text-white flex-shrink-0"
                >
                  {initials(selectedUser.name, '')}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <ChartLineUp size={16} weight="bold" className="text-[#0055ff] flex-shrink-0" />
                    <span className="font-display font-bold truncate">{selectedUser.name}</span>
                  </div>
                  <p className="text-[11px] text-zinc-500">{t('stats.activityTitle')} · {periodLabel(period)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedUser(null)}
                  className="p-1.5 rounded-sm border border-zinc-200 text-zinc-500 hover:bg-zinc-50 hover:text-zinc-800"
                  aria-label={t('stats.closeChart')}
                  data-testid="stats-activity-close"
                >
                  <X size={14} />
                </button>
              </div>
              <div className="flex items-center gap-4 mb-3 text-[10px] uppercase tracking-wider font-semibold">
                <span className="flex items-center gap-1.5 text-[#0055ff]">
                  <span className="w-3 h-3 rounded-sm bg-[#0055ff]/35 border border-[#0055ff]" />
                  {t('stats.linesAdded')}
                </span>
                <span className="flex items-center gap-1.5 text-orange-600">
                  <span className="w-3 h-0.5 bg-orange-600 rounded" />
                  {t('stats.commits')}
                </span>
              </div>
              <ActivityTimelineChart
                points={activityTimeline?.points || []}
                loading={activityLoading}
                commitsLabel={t('stats.commits')}
                linesLabel={t('stats.linesAdded')}
                emptyLabel={t('stats.activityEmpty')}
              />
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
